/**
 * Helpers to resolve content-bank labels and categories via i18n with fallback to API/raw values.
 * Used by games that consume content from the backend content bank (Word Fluency Tap, N-Back, etc.).
 */

import type { TFunction } from "i18next";

const CONTENT_BANK_NS = "games.contentBank";

/** Item IDs from asset manifest follow pattern img_0001, img_0002, ... */
const CONTENT_BANK_ITEM_ID_RE = /^img_\d+$/;

export function isContentBankItemId(id: string): boolean {
  return typeof id === "string" && CONTENT_BANK_ITEM_ID_RE.test(id);
}

/**
 * Returns translated label for a content-bank item (asset), or fallback when key is missing.
 * Use when displaying item/tile labels that may come from the content bank (e.g. itemId from API).
 */
export function getContentBankLabel(
  t: TFunction,
  id: string,
  fallback: string
): string {
  if (!id || !isContentBankItemId(id)) return fallback;
  const key = `${CONTENT_BANK_NS}.assets.labels.${id}`;
  const out = t(key);
  return out && out !== key ? out : fallback;
}

/**
 * Returns translated label for a content-bank category slug, or fallback when key is missing.
 * Use for category prompts (e.g. "Tap all animals") when the category comes from the content bank.
 */
export function getContentBankCategoryLabel(
  t: TFunction,
  slug: string,
  fallback: string
): string {
  if (!slug || typeof slug !== "string") return fallback;
  const key = `${CONTENT_BANK_NS}.assets.categories.${slug}`;
  const out = t(key);
  return out && out !== key ? out : fallback;
}

/** Rule Switch bin / object attribute slugs (matches ruleSwitchContent pools). */
export type RuleSwitchAttributeRule = "color" | "shape" | "size" | "pattern";

/**
 * Localized display string for a Rule Switch attribute value (e.g. colour name, shape).
 * Keys live under content-bank `ruleSwitch.{rule}.{slug}`.
 */
export function getRuleSwitchAttributeLabel(
  t: TFunction,
  rule: RuleSwitchAttributeRule,
  valueSlug: string
): string {
  if (!valueSlug) return valueSlug;
  const key = `${CONTENT_BANK_NS}.ruleSwitch.${rule}.${valueSlug}`;
  const out = t(key);
  return out && out !== key ? out : valueSlug;
}
